"use client";

import * as React from "react";
import { cn } from "@/lib/utils";

const SIZES = {
  xs: 32,
  sm: 48,
  md: 64,
  lg: 80,
} as const;

const DEFAULT_STROKE_WIDTH = {
  xs: 3,
  sm: 4,
  md: 5,
  lg: 6,
} as const;

type Size = keyof typeof SIZES;

interface CircularProgressProps {
  /** Progress value from 0 to 100 */
  value: number;
  /** Size of the circular progress indicator */
  size?: Size;
  /** Custom stroke width (defaults based on size) */
  strokeWidth?: number;
  /** Show the percentage value in the center */
  showValue?: boolean;
  /** Custom label displayed below the value */
  label?: string;
  /** Custom content to display in the center (overrides showValue) */
  children?: React.ReactNode;
  /** Additional CSS classes */
  className?: string;
  /** Use success color (cede-success) instead of primary */
  isHighProgress?: boolean;
  /** Animate the progress from 0 on mount */
  animate?: boolean;
}

/**
 * CircularProgress - A circular progress indicator for displaying completion metrics.
 *
 * Features:
 * - SVG-based circular progress with smooth animation
 * - Three predefined sizes (sm, md, lg)
 * - Support for custom center content or automatic value display
 * - Success color variant for high progress states
 * - Respects prefers-reduced-motion for accessibility
 * - Subtle glow effect on the progress arc
 */
const CircularProgress = React.forwardRef<HTMLDivElement, CircularProgressProps>(
  (
    {
      value,
      size = "md",
      strokeWidth: customStrokeWidth,
      showValue = false,
      label,
      children,
      className,
      isHighProgress = false,
      animate = true,
    },
    ref,
  ) => {
    const [animatedValue, setAnimatedValue] = React.useState(animate ? 0 : value);
    const animationRef = React.useRef<number | null>(null);
    const prefersReducedMotion = React.useRef(false);

    // Clamp value between 0 and 100
    const clampedValue = Math.min(100, Math.max(0, value));

    const dimension = SIZES[size];
    const strokeWidth = customStrokeWidth ?? DEFAULT_STROKE_WIDTH[size];
    const radius = (dimension - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const center = dimension / 2;

    // Calculate stroke dash offset based on animated value
    const strokeDashoffset = circumference - (animatedValue / 100) * circumference;

    // Determine progress color
    const progressColor = isHighProgress ? "var(--color-cede-success)" : "var(--color-cede-primary)";
    const glowColor = isHighProgress ? "rgba(166, 251, 116, 0.4)" : "rgba(94, 166, 255, 0.4)";

    // Check for reduced motion preference
    React.useEffect(() => {
      if (typeof window !== "undefined") {
        prefersReducedMotion.current = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      }
    }, []);

    // Animate from 0 to value on mount
    React.useEffect(() => {
      if (!animate || prefersReducedMotion.current) {
        setAnimatedValue(clampedValue);
        return;
      }

      const startTime = performance.now();
      const startValue = 0;
      const endValue = clampedValue;
      const duration = 800; // Animation duration in ms

      const animateProgress = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Ease-out cubic for smooth deceleration
        const easeOut = 1 - Math.pow(1 - progress, 3);

        const currentValue = startValue + (endValue - startValue) * easeOut;
        setAnimatedValue(currentValue);

        if (progress < 1) {
          animationRef.current = requestAnimationFrame(animateProgress);
        } else {
          setAnimatedValue(endValue);
          animationRef.current = null;
        }
      };

      animationRef.current = requestAnimationFrame(animateProgress);

      return () => {
        if (animationRef.current !== null) {
          cancelAnimationFrame(animationRef.current);
        }
      };
    }, [clampedValue, animate]);

    // Update animated value when value prop changes (after initial animation)
    React.useEffect(() => {
      if (!animate) {
        setAnimatedValue(clampedValue);
      }
    }, [clampedValue, animate]);

    // Text size based on component size
    const getValueFontSize = () => {
      const rounded = Math.round(animatedValue);
      switch (size) {
        case "xs":
          return rounded >= 100 ? "text-[8px]" : "text-[10px]";
        case "sm":
          return "text-xs";
        case "md":
          return "text-sm";
        case "lg":
          return "text-base";
        default:
          return "text-sm";
      }
    };

    const getLabelFontSize = () => {
      switch (size) {
        case "xs":
          return "text-[6px]";
        case "sm":
          return "text-[8px]";
        case "md":
          return "text-[10px]";
        case "lg":
          return "text-xs";
        default:
          return "text-[10px]";
      }
    };

    return (
      <div ref={ref} className={cn("relative inline-flex items-center justify-center", className)}>
        <svg
          width={dimension}
          height={dimension}
          viewBox={`0 0 ${dimension} ${dimension}`}
          className="transform -rotate-90"
        >
          {/* Glow filter for progress arc */}
          <defs>
            <filter
              id={`glow-${size}-${isHighProgress ? "success" : "primary"}`}
              x="-50%"
              y="-50%"
              width="200%"
              height="200%"
            >
              <feGaussianBlur stdDeviation="2" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Track (background circle) */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="var(--color-cede-bg-component)"
            strokeWidth={strokeWidth}
            className="opacity-100"
          />

          {/* Progress arc */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke={progressColor}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            filter={`url(#glow-${size}-${isHighProgress ? "success" : "primary"})`}
            style={{
              filter: `drop-shadow(0 0 4px ${glowColor})`,
              transition: animate ? "none" : "stroke-dashoffset 0.3s ease-out",
            }}
          />
        </svg>

        {/* Center content */}
        {(children || showValue || label) && (
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            {children ? (
              children
            ) : (
              <>
                {showValue && (
                  <span className={cn("font-semibold text-cede-text-primary", getValueFontSize())}>
                    {Math.round(animatedValue)}%
                  </span>
                )}
                {label && (
                  <span className={cn("text-cede-text-secondary leading-tight", getLabelFontSize())}>{label}</span>
                )}
              </>
            )}
          </div>
        )}
      </div>
    );
  },
);

CircularProgress.displayName = "CircularProgress";

export { CircularProgress };
export type { CircularProgressProps };
